/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import React, { useEffect, useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { DetailsContext } from '../../context/detailsContext';
import { TopicContext } from '../../context/topicContext';
import Pillar from '../Pillar';
import { headerFadeoutDuration } from '../../constants';
import { messages as text } from '../../messages';
import { tracking, tagType } from '../../utils/tracking';
import {
  viewDetailsContainer,
  sceneStyling,
  averageStyling,
  averageWrapperStyling,
  h3,
  leftBarAnimation,
  rightBarAnimation,
} from './styling';

const ViewDetails = ({ messages, hasScaledPillar }) => {
  const { currentIndex, viewDetails, fadeOutDetails } = useContext(
    DetailsContext,
  );
  const { currentTopic } = useContext(TopicContext);

  const [isAnimate, setIsAnimate] = useState(false);
  const [topicIndex, setTopicIndex] = useState(currentIndex);

  const multiplier = 5;
  const color = messages.charts[topicIndex]?.color;
  const overall = messages.charts[topicIndex]?.overall;
  const bar1 = messages.charts[topicIndex]?.details.bar1;
  const bar2 = messages.charts[topicIndex]?.details.bar2;
  const bar3 = messages.charts[topicIndex]?.details.bar3;

  // ref for storing timeout when header text should fade in/out
  let timeoutRef = useRef();

  // Transition from overview to detail
  useEffect(() => {
    if (viewDetails) {
      setIsAnimate(false);

      setTimeout(() => {
        tracking(
          tagType.pageView,
          text[`topic${topicIndex}Title`].defaultMessage,
        );
        setIsAnimate(true);
      }, headerFadeoutDuration);
    }
  }, [viewDetails]);

  // Transition from detail to detail
  useEffect(() => {
    if (viewDetails) {
      setIsAnimate(false);

      setTimeout(() => {
        tracking(
          tagType.pageView,
          text[`topic${topicIndex}Title`].defaultMessage,
        );
        setIsAnimate(true);
      }, headerFadeoutDuration);
    }
  }, [currentIndex]);

  useEffect(() => {
    if (viewDetails) {
      // If not same topic start by clearing timeout, maybe the user has clicked back and forth quickly without the timer ended.
      if (currentTopic !== topicIndex) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Set a timeout, same duration as the css transition, (remember subtitle has 100ms delay)
        // And set animating to false, meaning fade new/updated text in.
        timeoutRef.current = setTimeout(() => {
          setTopicIndex(currentTopic);
        }, headerFadeoutDuration);
      }
    } else {
      setTopicIndex(currentIndex);
    }
  }, [currentTopic, currentIndex]);

  return (
    <div
      role="img"
      aria-label="3 bars and a line display the average and percent breakdown by respondent type"
      css={viewDetailsContainer}
      style={{
        zIndex: viewDetails ? (fadeOutDetails ? 0 : 5) : 0,
        opacity: viewDetails ? (fadeOutDetails ? 0 : 1) : 0,
        pointerEvents: viewDetails
          ? fadeOutDetails
            ? 'none'
            : 'auto'
          : 'none',
      }}>
      <div css={[sceneStyling]} style={{ opacity: isAnimate ? 1 : 0 }}>
        <div css={leftBarAnimation(isAnimate)}>
          <Pillar
            color={color}
            totalPercent={bar1 * multiplier}
            shownPercent={bar1}
            hasScaledPillar={hasScaledPillar}
            isVisible={isAnimate}
            scaleSizeX={1}
            scaleSizeY={1}
            current={isAnimate}
            pillarHeightInPixel={200}
            origWidthOfPillar={36}
            scaledWidthPixelSize={21}
            showPillarGradient={true}
          />
        </div>

        <div>
          <Pillar
            color={color}
            totalPercent={bar2 * multiplier}
            shownPercent={bar2}
            hasScaledPillar={hasScaledPillar}
            isVisible={isAnimate}
            scaleSizeX={1}
            scaleSizeY={1}
            current={isAnimate}
            pillarHeightInPixel={200}
            origWidthOfPillar={36}
            scaledWidthPixelSize={21}
            showPillarGradient={true}
          />
        </div>

        <div css={rightBarAnimation(isAnimate)}>
          <Pillar
            color={color}
            totalPercent={bar3 * multiplier}
            shownPercent={bar3}
            hasScaledPillar={hasScaledPillar}
            isVisible={isAnimate}
            scaleSizeX={1}
            scaleSizeY={1}
            current={isAnimate}
            pillarHeightInPixel={200}
            origWidthOfPillar={36}
            scaledWidthPixelSize={21}
            showPillarGradient={true}
          />
        </div>
      </div>
      {viewDetails && (
        <div
          css={[averageStyling(overall * multiplier)]}
          style={{ opacity: isAnimate ? 1 : 0 }}>
          <div css={[averageWrapperStyling(isAnimate)]}>
            <span>{`${overall}%`}</span>
            <span>
              <FormattedMessage id="detail.detailAverageLabel" />
            </span>
          </div>
          <h3 css={h3(isAnimate)}>
            <span>
              <FormattedMessage id="detail.detailDevExecLabel" />
            </span>
            <span>
              <FormattedMessage id="detail.detailDevLabel" />
            </span>
            <span>
              <FormattedMessage id="detail.detailIt" />
            </span>
          </h3>
        </div>
      )}
    </div>
  );
};

ViewDetails.propTypes = {
  messages: PropTypes.object,
  hasScaledPillar: PropTypes.bool,
};

export default React.forwardRef(ViewDetails);
